import "./TableV2Component.scss";
import {Row, useBlockLayout, useExpanded, useTable} from 'react-table';
import {useSticky} from "react-table-sticky";
import React, {useCallback, useMemo} from "react";
import {TableStyles} from "./TableStyles";
import _ from "lodash";
import {ITableColumn, ITableComponentProps} from "../../../models/table.model";
import StatusCardComponent from "../../status-card/statusCardComponent";
import {CommonService} from "../../../services";
import LoaderComponent from "../../loader/loaderComponent";
import {useVirtual} from "react-virtual";

const MAXIMUM_ROWS_REACHED = 200;

interface TableV2ComponentProps extends ITableComponentProps {
    loading?: boolean;
    errored?: boolean;
    id?: string;
    columns: ITableColumn[];
    data: any[];
    noDataText?: string
}

const TableV2Component = (props: TableV2ComponentProps) => {

    const {
        noDataText,
        loading,
        errored,
        hideHeader,
        columns,
        defaultExpandAllRows,
        expandRowRenderer,
        canExpandRow,
        onRowClick,
        data,
        sort,
        onSort
    } = props;

    const size = props.size || "medium";

    const parseRender = useCallback((col: ITableColumn, item: any) => {
        const data = item.row.original;
        const index = item.row.index;
        if (col.content) {
            return col.content(data, index);
        }
    }, []);

    const TransformColumn = useCallback((column: ITableColumn) => {
        const colObject: any = {
            Header: column?.label || " ",
            key: column?.id,
            align: column?.align || "left",
            accessor: column?.id,
            sticky: column?.fixed,
            sortable: column?.sortable,
            width: 150,
        };
        if (column?.content) {
            colObject['Cell'] = (data: any) => parseRender(column, data);
        }
        if (column?.width) {
            colObject['width'] = column?.width;
        }
        if (column?.children) {
            colObject['columns'] = column.children.map((child: ITableColumn) => TransformColumn(child));
        }
        return colObject;
    }, [parseRender]);

    const parseColumns = useCallback((columns: ITableColumn[]) => {
        const transformedCols: any = columns.map((column: ITableColumn) => {
            return TransformColumn(column);
        });
        return transformedCols;
    }, [TransformColumn]);

    const columnsMemoized = useMemo<any>(() =>
        parseColumns(columns), [columns, parseColumns]);

    const dataMemoized = useMemo<any>(() =>
        data || [], [data]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns: columnsMemoized,
            data: dataMemoized,
        },
        useBlockLayout,
        useExpanded,
        useSticky
    );

    //virtualization code;
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const tableStylesRef = React.useRef<HTMLDivElement>(null);
    const virtualizationEnabled = rows.length > MAXIMUM_ROWS_REACHED;

    const rowVirtualizer = useVirtual({
        parentRef: tableContainerRef,
        size: rows.length,
        estimateSize: React.useCallback(() => 52, []),
        overscan: 10,
    });

    const {virtualItems: virtualRows, totalSize} = rowVirtualizer;
    const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    const paddingBottom =
        virtualRows.length > 0
            ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
            : 0;

    const getTHClasses = useCallback((column: ITableColumn) => {
        let classes = 'th t-th t-cell t-cell-' + column.key?.split(' ').join('-') + " " + column.className + ' t-cell-align-' + column.align;
        if (column?.sortable) {
            classes += " sortable";
            if (sort && sort.key === column.key) {
                if (sort.order === "asc") {
                    classes += " sort-asc";
                } else {
                    classes += " sort-desc";
                }
            }
        }
        return classes;
    }, [sort]);

    const getTDClasses = useCallback((column: ITableColumn) => {
        return 'td t-td t-cell t-cell-' + column.key?.split(' ').join('-') + " " + column.className + ' t-cell-align-' + column.align;
    }, []);

    const handleRowClick = useCallback((row: any) => {
        if (onRowClick) {
            const data = row.original;
            const index = row.index;
            onRowClick(data, index);
        }
    }, [onRowClick]);

    const applySort = useCallback((column: ITableColumn) => {
        if (!column.sortable || !sort) return;
        const sortObj: any = _.cloneDeep(sort);
        if (sortObj.key === column.key) {
            sortObj.key = column.key;
            if (sortObj.order === "asc") {
                sortObj.order = "desc";
            } else {
                sortObj.order = "";
                sortObj.key = "";
            }
        } else {
            sortObj.key = column.key;
            sortObj.order = "asc";
        }
        if (onSort) {
            onSort(sortObj.key, sortObj.order);
        }
    }, [sort, onSort]);

    return (
        <div className={'table-component'}>
            <TableStyles className={'styled-table'} ref={tableStylesRef}>
                <div className={`t-table-wrapper`}>
                    <div {...getTableProps()} className={`t-table table sticky ${size}`}
                         ref={virtualizationEnabled ? tableContainerRef : undefined}
                         style={virtualizationEnabled ? {height: tableStylesRef?.current?.clientHeight} : undefined}
                    >
                        {!hideHeader && <div className="header t-thead">
                            {headerGroups.map((headerGroup: any) => (
                                <div {...headerGroup.getHeaderGroupProps()}
                                     key={CommonService.getUUID()}
                                     className="t-tr">
                                    {headerGroup.headers.map((column: any) => <div {...column.getHeaderProps()}
                                                                                   key={CommonService.getUUID()}
                                                                                   onClick={() => applySort(column)}
                                                                                   className={getTHClasses(column)}
                                        >
                                            {column.render('Header')}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        }
                        {
                            loading && <div >
                                <LoaderComponent size={'m'}/>
                            </div>
                        }
                        {
                            !errored && <div {...getTableBodyProps()} className="body t-body">
                                {paddingTop > 0 && virtualizationEnabled && (
                                    <div style={{height: `${paddingTop}px`}}/>
                                )}
                                {
                                    rows?.length > 0 && !virtualizationEnabled && rows.map((row: any, index) => {
                                        prepareRow(row);
                                        return (
                                            <React.Fragment key={index}>
                                                <div className="t-tr"
                                                     key={CommonService.getUUID()}
                                                     onClick={() => handleRowClick(row)} {...row.getRowProps()}>
                                                    {row.cells.map((cell: any) => {
                                                        return (
                                                            <div {...cell.getCellProps()}
                                                                 key={CommonService.getUUID()}
                                                                 className={getTDClasses(cell.column)}>
                                                                {cell.render('Cell')}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                                {(defaultExpandAllRows && (canExpandRow && canExpandRow(row.original))) && expandRowRenderer &&
                                                    <div
                                                        key={CommonService.getUUID()}
                                                        className={'t-tr t-tr-expand'}>
                                                        <div className="t-td">
                                                            {expandRowRenderer(row.original, row.index)}
                                                        </div>
                                                    </div>
                                                }
                                            </React.Fragment>
                                        );
                                    })
                                }
                                {
                                    rows?.length > 0 && virtualizationEnabled && virtualRows.map((virtualRow, index) => {
                                        const row = rows[virtualRow.index] as Row<any>;
                                        prepareRow(row);
                                        return (
                                            <React.Fragment key={index}>
                                                <div className="t-tr"
                                                     ref={virtualRows[index].measureRef}
                                                     onClick={() => handleRowClick(row)} {...row.getRowProps()}>
                                                    {row.cells.map((cell: any) => {
                                                        return (
                                                            <div {...cell.getCellProps()}
                                                                 key={CommonService.getUUID()}
                                                                 className={getTDClasses(cell.column)}>
                                                                {cell.render('Cell')}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                                {(defaultExpandAllRows && (canExpandRow && canExpandRow(row.original))) && expandRowRenderer &&
                                                    <div
                                                        key={CommonService.getUUID()}
                                                        className={'t-tr t-tr-expand'}>
                                                        <div className="t-td">
                                                            {expandRowRenderer(row.original, row.index)}
                                                        </div>
                                                    </div>
                                                }
                                            </React.Fragment>
                                        );
                                    })
                                }
                                {paddingBottom > 0 && virtualizationEnabled && (
                                    <div style={{height: `${paddingBottom}px`}}/>
                                )}
                                {
                                    (rows?.length === 0 && !loading) &&
                                    <StatusCardComponent title={noDataText ? noDataText : "No data found"}
                                                         className={'table-data-not-found-card'}/>
                                }
                            </div>
                        }
                        {
                            (errored && !loading) &&
                            <StatusCardComponent title={"Error loading data"} className={'table-loading-error-card'}/>
                        }
                    </div>
                </div>
            </TableStyles>
        </div>
    );
};

export default TableV2Component;
